import { useState } from "react";
import { RotateCcw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  useRefineTemplate,
  type CampaignBodyFormat,
  type RefineMode,
} from "@/features/leads/api/campaigns";

/**
 * „Redaguj AI" dla szablonu kampanii.
 *
 * Mail piszemy swoimi słowami, model ubiera go w formę — i NIC nie zmienia
 * merytorycznie. Serwer pilnuje tego po fakcie: znaczniki `{{…}}` i liczby
 * muszą przetrwać co do znaku, inaczej wraca oryginał z notą, którą tu widać.
 *
 * Podmiana jest cofalna jednym kliknięciem. Szablon to kilkanaście minut pisania
 * i idzie do setek firm — „nie podoba mi się, jak to przerobiło" nie może
 * oznaczać pisania od nowa.
 */

/** Komunikat z koperty { data, errors, meta }; ogólnik dopiero, gdy serwer milczy. */
function refineError(err: unknown): string {
  const errors = (err as { response?: { data?: { errors?: unknown } } })?.response
    ?.data?.errors;
  const text =
    typeof errors === "string"
      ? errors
      : errors && typeof errors === "object"
        ? Object.values(errors as Record<string, unknown>)
            .flat()
            .join(" ")
        : "";
  return text || "Redakcja AI nie powiodła się — spróbuj za chwilę.";
}

const MODE_LABELS: Record<RefineMode, string> = {
  standard: "standardowy",
  formal: "formalny",
};

/** Wybór stylu — jeden na całe okno kampanii (treść i przypomnienie razem). */
export function RefineStyleSelect({
  value,
  onChange,
}: {
  value: RefineMode;
  onChange: (mode: RefineMode) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as RefineMode)}
      className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-0.5 text-[11px] text-[var(--color-text-secondary)]"
      title="Styl redakcji AI"
    >
      {(Object.keys(MODE_LABELS) as RefineMode[]).map((mode) => (
        <option key={mode} value={mode}>
          styl: {MODE_LABELS[mode]}
        </option>
      ))}
    </select>
  );
}

export function RefineButton({
  body,
  format,
  mode,
  onRefined,
  disabled,
}: {
  body: string;
  format: CampaignBodyFormat;
  mode: RefineMode;
  onRefined: (next: string) => void;
  disabled?: boolean;
}) {
  const refine = useRefineTemplate();
  const [note, setNote] = useState("");
  // Poprzednia wersja żyje tylko do najbliższej zmiany treści — po ręcznej
  // poprawce „Cofnij" skasowałoby ją razem z redakcją.
  const [previous, setPrevious] = useState<string | null>(null);

  const run = () => {
    setNote("");
    const before = body;
    refine.mutate(
      { body: before, mode, body_format: format },
      {
        onSuccess: (result) => {
          if (result.changed) {
            setPrevious(before);
            onRefined(result.body);
          }
          setNote(result.note);
        },
        // Serwer mówi KONKRETNIE, co poszło nie tak (odmowa modelu, za długi
        // szablon, awaria dostawcy). Podmiana tego na jedno „spróbuj za chwilę"
        // kazała czekać także wtedy, gdy czekanie nic nie zmieni.
        onError: (err) => setNote(refineError(err)),
      },
    );
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={run}
        loading={refine.isPending}
        disabled={disabled || !body.trim()}
        title="AI poprawia wyłącznie formę — znaczniki, liczby i terminy zostają co do znaku."
      >
        <Sparkles size={13} className="mr-1" />
        Redaguj AI
      </Button>
      {previous !== null && previous !== body && (
        <button
          type="button"
          onClick={() => {
            onRefined(previous);
            setPrevious(null);
            setNote("");
          }}
          className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
        >
          <RotateCcw size={11} /> Cofnij redakcję
        </button>
      )}
      {note && <span className="text-[11px] text-[var(--color-text-muted)]">{note}</span>}
    </span>
  );
}
